import React, { useState } from "react";
import styles from "./styles.module.scss";
import theme from "../../Styles/theme.scss";
import {
  VictoryChart,
  VictoryGroup,
  VictoryBar,
  VictoryAxis,
  VictoryContainer,
  VictoryTooltip,
  VictoryLabel,
} from "victory";
import { AutoSizer } from "react-virtualized";
import {
  buildChartBuckets,
  formatRangeLabel,
  isWeekendBucketKey,
} from "../../Pages/InstallPerformance/utils";
import type { Granularity } from "@intelligentlilli/api-layer";
import type { InstallPeriodType } from "@intelligentlilli/api-layer";
import { useAmplitudeTracking } from "Services/Hooks/useAmplitudeTracking";

interface InstallPerformanceChartProps {
  periods: InstallPeriodType[];
  granularity: Granularity;
  range?: { start: string; end: string };
  timezone?: string;
}

const FONT_FAMILY = "Inter";

type SeriesKey = "started" | "referred" | "failed" | "succeeded";

// Bar colours come from the brand theme (src/Styles/theme.scss)
const BAR_SERIES: {
  key: SeriesKey;
  label: string;
  color: string;
}[] = [
  { key: "referred", label: "Residents created", color: theme.secondary3 },
  // { key: "started", label: "Install started", color: theme.unexpected3 },
  { key: "failed", label: "Install failed", color: theme.risk3 },
  { key: "succeeded", label: "Install complete", color: theme.expected2 },
];

// Renders the count permanently above each bar AND a tooltip on hover. A bar
// only takes one labelComponent, so this wraps both; copying VictoryTooltip's
// defaultEvents is what wires up the hover activation. When `showCount` is
// false (bars too narrow to fit the numbers) only the tooltip is rendered.
const BarLabel = (props: any) => {
  const { seriesLabel, showCount, ...rest } = props;
  return (
    <g>
      {showCount && (
        <VictoryLabel
          {...rest}
          dy={-4}
          style={{ fontFamily: FONT_FAMILY, fontSize: 9, fill: theme.neutral6 }}
        />
      )}
      <VictoryTooltip
        {...rest}
        text={({ datum }: any) => `${seriesLabel}: ${datum._y}`}
        constrainToVisibleArea
        pointerLength={4}
        flyoutStyle={{
          fill: theme.white,
          stroke: theme.neutral3,
          strokeWidth: 1,
        }}
        flyoutPadding={{ top: 6, bottom: 6, left: 10, right: 10 }}
        style={{
          fontFamily: FONT_FAMILY,
          fontSize: 11,
          fill: theme.neutral7,
        }}
      />
    </g>
  );
};
BarLabel.defaultEvents = (VictoryTooltip as any).defaultEvents;

const InstallPerformanceChart = ({
  periods,
  granularity,
  range,
  timezone,
}: InstallPerformanceChartProps) => {
  const [visibleCategories, setVisibleCategories] = useState<
    Record<SeriesKey, boolean>
  >({ started: true, referred: true, failed: true, succeeded: true });
  const [hideWeekends, setHideWeekends] = useState(true);
  const track = useAmplitudeTracking();

  const toggleCategory = (key: SeriesKey) => {
    const willBeVisible = !visibleCategories[key];
    setVisibleCategories((prev) => ({ ...prev, [key]: !prev[key] }));
    track("Install Performance", {
      action: "chart_category_toggled",
      category: key,
      category_label:
        BAR_SERIES.find((series) => series.key === key)?.label ?? key,
      state: willBeVisible ? "ticked" : "unticked",
      granularity,
    });
  };

  const allBuckets = buildChartBuckets(periods, granularity, range, timezone);
  // Weekends are only meaningful at day granularity; weekly/monthly buckets
  // always show everything.
  const buckets =
    granularity === "D" && hideWeekends
      ? allBuckets.filter((b) => !isWeekendBucketKey(b.key))
      : allBuckets;

  // Judged on the unfiltered buckets so hiding weekends can't collapse the
  // chart into the empty state (which has no checkbox to toggle back).
  const totalActivity = allBuckets.reduce(
    (sum, b) => sum + b.started + b.referred + b.failed + b.succeeded,
    0
  );

  const rangeLabel = range ? formatRangeLabel(range, granularity) : null;

  if (allBuckets.length === 0 || totalActivity === 0) {
    return (
      <div className={styles.container}>
        <h2 className={styles.title}>Install performance</h2>
        {rangeLabel && <p className={styles.subtitle}>{rangeLabel}</p>}
        <div className={styles.empty}>No install activity for this period</div>
      </div>
    );
  }

  const chartData = buckets.map((bucket) => ({
    x: bucket.label,
    started: bucket.started,
    referred: bucket.referred,
    failed: bucket.failed,
    succeeded: bucket.succeeded,
  }));

  const visibleSeries = BAR_SERIES.filter(
    (series) => visibleCategories[series.key]
  );

  const maxValue = Math.max(
    ...chartData.flatMap((d) => visibleSeries.map((s) => d[s.key])),
    1
  );

  return (
    <div className={styles.container}>
      <h2 className={styles.title}>Install performance</h2>
      {rangeLabel && <p className={styles.subtitle}>{rangeLabel}</p>}
      <div className={styles.legend_filters}>
        {BAR_SERIES.map((series) => (
          <label key={series.key} className={styles.legend_item}>
            <input
              type="checkbox"
              checked={visibleCategories[series.key]}
              onChange={() => toggleCategory(series.key)}
            />
            <span
              className={styles.legend_swatch}
              style={{ backgroundColor: series.color }}
            />
            {series.label}
          </label>
        ))}
        {granularity === "D" && (
          <label className={`${styles.legend_item} ${styles.weekend_toggle}`}>
            <input
              type="checkbox"
              checked={hideWeekends}
              onChange={() => setHideWeekends((prev) => !prev)}
            />
            Hide weekends
          </label>
        )}
      </div>
      {visibleSeries.length === 0 && (
        <div className={styles.empty}>No categories selected</div>
      )}
      {visibleSeries.length > 0 && (
      <div className={styles.chart_wrapper}>
        <AutoSizer disableHeight>
          {({ width }: { width: number }) => {
            // Guarantee each bucket enough room for its grouped bars and
            // angled label. When the container is too narrow (e.g. the
            // 12-month view), the chart grows beyond it and the wrapper
            // scrolls horizontally instead of squishing the labels.
            const MIN_BUCKET_WIDTH = 80;
            const chartWidth = Math.max(width, buckets.length * MIN_BUCKET_WIDTH);
            const barWidth = Math.max(
              6,
              Math.min(20, Math.floor(chartWidth / (buckets.length * 6)))
            );
            const chartHeight = 450;
            // Adjacent bar labels are (barWidth + 2)px apart. Only render the
            // permanent counts when the widest count (~6px per digit at 9px
            // font) fits in that gap; otherwise the hover tooltip covers it.
            const showCounts = barWidth + 2 > String(maxValue).length * 6;

            return (
              <VictoryChart
                width={chartWidth}
                height={chartHeight}
                domainPadding={{ x: barWidth * 3 }}
                // Extra bottom padding so the angled bodyMedium x-axis labels
                // (e.g. "May 2026" on the 12-month preset) aren't clipped.
                padding={{ top: 20, bottom: 100, left: 55, right: 20 }}
                // AutoSizer's own div is zero-width (it measures the parent),
                // so Victory's default responsive svg (width: 100%) collapses
                // to 0px. Render at fixed pixel size instead.
                containerComponent={<VictoryContainer responsive={false} />}
              >
                <VictoryAxis
                  tickValues={chartData.map((d) => d.x)}
                  style={{
                    axis: { stroke: theme.neutral3 },
                    tickLabels: {
                      fontFamily: FONT_FAMILY,
                      fontSize: theme.bodyMedium,
                      fill: theme.neutral5,
                      angle: buckets.length > 7 ? -45 : 0,
                      textAnchor: buckets.length > 7 ? "end" : "middle",
                    },
                    grid: { stroke: "none" },
                  }}
                />
                <VictoryAxis
                  dependentAxis
                  tickFormat={(t: number) =>
                    Number.isInteger(t) ? String(t) : ""
                  }
                  domain={[0, maxValue]}
                  style={{
                    axis: { stroke: theme.neutral3 },
                    tickLabels: {
                      fontFamily: FONT_FAMILY,
                      fontSize: theme.bodyMedium,
                      fill: theme.neutral5,
                    },
                    grid: {
                      stroke: theme.neutral2,
                      strokeDasharray: "4,4",
                    },
                  }}
                />
                <VictoryGroup offset={barWidth + 2}>
                  {visibleSeries.map((series) => (
                    <VictoryBar
                      key={series.key}
                      data={chartData}
                      x="x"
                      y={series.key}
                      barWidth={barWidth}
                      cornerRadius={{ top: 2 }}
                      style={{ data: { fill: series.color } }}
                      labels={({ datum }: { datum: Record<string, number> }) =>
                        datum[series.key] > 0 ? String(datum[series.key]) : ""
                      }
                      labelComponent={
                        <BarLabel
                          seriesLabel={series.label}
                          showCount={showCounts}
                        />
                      }
                    />
                  ))}
                </VictoryGroup>
              </VictoryChart>
            );
          }}
        </AutoSizer>
      </div>
      )}
      <p className={styles.note}>
        Note: residents created in one month may not always resolve to a completed install in the same week or
        month due to scheduling and availability.
      </p>
    </div>
  );
};

export default InstallPerformanceChart;
